import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { OktaAuth } from '@okta/okta-auth-js';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class OktaService {
  private oktaAuth: OktaAuth;
  private isAuthenticated = new BehaviorSubject<boolean>(false);

  constructor() {
    // Simple Okta configuration
    this.oktaAuth = new OktaAuth({
      issuer: environment.oktaIssuer,
      clientId: environment.oktaClientId,
      redirectUri: environment.oktaRedirectUri,
      pkce: true,
      scopes:['openid', 'profile','email','offline_access'],
      tokenManager: {
        storage: 'localStorage',
        autoRenew: true,
        autoRemove: true,
      },
    });
    this.oktaAuth.tokenManager.on('expired', async (key, expiredToken) => {
      console.log(`Token ${key} expired.`);
      try {
        await this.oktaAuth.tokenManager.renew(key);
        console.log('Token successfully renewed.');
      } catch (error) {
        console.error('Token renewal failed:', error);
        await this.logout(); // Optional logout on failure
      }
    });
    
    this.oktaAuth.tokenManager.on('renewed', (key, newToken) => {
      console.log(`Token ${key} was renewed.`);

    });
    
    this.oktaAuth.tokenManager.on('error', (error) => {
      console.error('Token renewal error:', error);
      this.logout();
    });
    // Let Okta handle token refresh
    this.oktaAuth.start();
  }

  // Get current auth state
  isAuthenticated$(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  // Login
  login() {
    const originalUri = window.location.href; // Save the current URL
    this.oktaAuth.setOriginalUri(originalUri); // Set the original URI for Okta
    this.oktaAuth.signInWithRedirect();
  }

  // Logout
  async logout() {
    await this.oktaAuth.clearStorage();
    this.login();
  }


  isLoginRedirect(): boolean {
    return this.oktaAuth.isLoginRedirect();
  }

  getOriginalUri(): string | undefined {
    console.log("Current URI", this.oktaAuth.getOriginalUri());
    return this.oktaAuth.getOriginalUri();
  }
  
  clearOriginalUri(): void {
    this.oktaAuth.removeOriginalUri();
  }

  // Handle the redirect callback
  async handleCallback() {
    try {
      const { tokens } = await this.oktaAuth.token.parseFromUrl();
      await this.oktaAuth.tokenManager.setTokens(tokens);
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      this.isAuthenticated.next(isAuthenticated);
    } catch (error) {
      this.isAuthenticated.next(false);
    }
  }

  async checkAuthentication(): Promise<boolean> {
    try {
      const isAuthenticated = await this.oktaAuth.isAuthenticated();
      if (isAuthenticated) {
        const tokens = await this.oktaAuth.tokenManager.getTokens();
        if (!tokens.accessToken || !tokens.idToken) {
          // Tokens are missing or invalid
          this.isAuthenticated.next(isAuthenticated);
          return false;
        }
        this.isAuthenticated.next(isAuthenticated);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  // Get access token
  async getAccessToken(): Promise<string | null> {
    const token = await this.oktaAuth.getIdToken();
    return token || null;
  }
}